import { useState, useRef, useContext, useLayoutEffect, type FC } from "react";
import { isInsideSafeTriangle, isInsideRectangle } from "../../utils/helpers";
import type { NavigationHoverContextValue } from "./NavigationHoverContext";
import { NavigationHoverContext } from "./NavigationHoverContext";

type NavigationHoverContextProviderProps = {
  panelNode: HTMLElement | null;
};
export const NavigationHoverContextProvider: FC<
  NavigationHoverContextProviderProps
> = ({ children, panelNode }) => {
  const [hoveredElement, setHoveredElement] =
    useState<NavigationHoverContextValue["hoveredElement"]>(null);
  const [previousHoveredElement, setPreviousHoveredElement] =
    useState<NavigationHoverContextValue["hoveredElement"]>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const timeoutId = useRef<NodeJS.Timeout | null>(null);

  useLayoutEffect(() => {
    if (!hoveredElement) return;

    const panelEl = panelRef.current;

    const hidePanel = /* istanbul ignore next */ () => {
      setHoveredElement(null);
    };
    const onMouseMove = (e: MouseEvent) => {
      if (!panelEl || !hoveredElement) return;
      const panelRect = panelEl.getBoundingClientRect();
      const triggerRect = hoveredElement.getBoundingClientRect();
      const isHoverTrigger = isInsideRectangle(
        e.clientX,
        e.clientY,
        triggerRect.x,
        triggerRect.x + triggerRect.width,
        triggerRect.y,
        triggerRect.y + triggerRect.height
      );
      const isInsidePanel = isInsideRectangle(
        e.clientX,
        e.clientY,
        panelRect.x,
        panelRect.x + panelRect.width,
        panelRect.y,
        panelRect.y + panelRect.height
      );
      const isInside = isInsideSafeTriangle(
        e.clientX,
        e.clientY,
        panelRect.x,
        panelRect.y,
        panelRect.x,
        panelRect.y + panelRect.height,
        triggerRect.x,
        triggerRect.y
      );

      if (!isInside && !isHoverTrigger && !isInsidePanel) {
        hidePanel();
      }
    };
    window.addEventListener("mousemove", onMouseMove);

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
    };
  }, [hoveredElement, setHoveredElement]);

  const value = {
    panelRef,
    hoveredElement,
    previousHoveredElement,
    setHoveredElement(val: HTMLDivElement | null) {
      if (timeoutId.current !== null) {
        clearTimeout(timeoutId.current);
      }
      timeoutId.current = setTimeout(() => {
        if (val !== hoveredElement) {
          setHoveredElement((prevState) => {
            if (val !== prevState) {
              setPreviousHoveredElement(prevState);
              return val;
            }
            return prevState;
          });
        }
      }, 100);
    },
    panelNode,
  };

  return (
    <NavigationHoverContext.Provider value={value}>
      {children}
    </NavigationHoverContext.Provider>
  );
};

export const useNavigationHoverContext = (): NavigationHoverContextValue => {
  const context = useContext(NavigationHoverContext);
  if (context === undefined) {
    throw new Error(
      "useNavigationHoverContext must be used within a NavigationBarContextProvider"
    );
  }
  return context;
};
